import { useState, useRef, useCallback, useEffect } from 'react';

export const useAudioStreaming = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const playbackContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const audioQueue = useRef<ArrayBuffer[]>([]);
  const isPlayingRef = useRef(false);
  const isMutedRef = useRef(false);

  // Initialize audio contexts
  useEffect(() => {
    const initAudioContext = async () => {
      try {
        const context = new AudioContext({ sampleRate: 24000 });
        audioContextRef.current = context;
        
        // Add the audio worklet module
        await context.audioWorklet.addModule('/audio.worklet.js');

        // For playback
        playbackContextRef.current = new AudioContext({ sampleRate: 24000 });

      } catch (error) {
        console.error('Failed to initialize audio context:', error);
      }
    };
    
    initAudioContext();
    
    return () => {
      audioContextRef.current?.close();
      playbackContextRef.current?.close();
    };
  }, []);

  const startRecording = useCallback(async (onAudioData: (data: Int16Array) => void) => {
    try {
      if (!audioContextRef.current) {
        // Attempt to create it again if it's missing
        const context = new AudioContext({ sampleRate: 24000 });
        audioContextRef.current = context;
        await context.audioWorklet.addModule('/audio.worklet.js');
        console.log('AudioContext re-initialized.');
        throw new Error('Audio context not initialized');
      }
      // Resume context if it's suspended
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      streamRef.current = stream;
      
      const source = audioContextRef.current.createMediaStreamSource(stream);
      sourceRef.current = source;
      
      const workletNode = new AudioWorkletNode(audioContextRef.current, 'audio-processor');
      workletNodeRef.current = workletNode;

      workletNode.port.onmessage = (event) => {
        onAudioData(event.data);
      };

      isMutedRef.current = false;
      setIsMuted(false);
      
      source.connect(workletNode);
      workletNode.connect(audioContextRef.current.destination);
      
      setIsRecording(true);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      throw error;
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (workletNodeRef.current) {
      workletNodeRef.current.port.onmessage = null;
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    setIsRecording(false);
  }, []);

  const mute = useCallback(() => {
    if (workletNodeRef.current && !isMutedRef.current) {
      sourceRef.current?.disconnect(workletNodeRef.current);
      isMutedRef.current = true;
      setIsMuted(true);
      console.log('🎤 Audio muted');
    }
  }, []);

  const unmute = useCallback(() => {
    if (workletNodeRef.current && isMutedRef.current) {
      sourceRef.current?.connect(workletNodeRef.current);
      isMutedRef.current = false;
      setIsMuted(false);
      console.log('🎤 Audio unmuted');
    }
  }, []);

  const processAudioQueue = useCallback(() => {
    if (isPlayingRef.current || audioQueue.current.length === 0) {
      return;
    }

    isPlayingRef.current = true;
    setIsPlaying(true);

    const audioData = audioQueue.current.shift();
    if (!audioData || !playbackContextRef.current) {
      isPlayingRef.current = false;
      setIsPlaying(false);
      return;
    }

    try {
      const int16Array = new Int16Array(audioData);
      const float32Array = new Float32Array(int16Array.length);
      for (let i = 0; i < int16Array.length; i++) {
        float32Array[i] = int16Array[i] / 32768.0;
      }

      const audioBuffer = playbackContextRef.current.createBuffer(1, float32Array.length, 24000);
      audioBuffer.getChannelData(0).set(float32Array);

      const source = playbackContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(playbackContextRef.current.destination);
      
      source.onended = () => {
        isPlayingRef.current = false;
        setIsPlaying(false);
        processAudioQueue(); // Process next chunk
      };
      
      source.start();

    } catch (error) {
      console.error('Error playing audio chunk:', error);
      isPlayingRef.current = false;
      setIsPlaying(false);
    }
  }, []);

  const playAudioChunk = useCallback((audioData: ArrayBuffer) => {
    audioQueue.current.push(audioData);
    if (!isPlayingRef.current) {
      processAudioQueue();
    }
  }, [processAudioQueue]);

  return {
    isRecording,
    isMuted,
    isPlaying,
    startRecording,
    stopRecording,
    mute,
    unmute,
    playAudioChunk
  };
};
